import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaArrowLeft, FaDownload } from 'react-icons/fa';
import '../../../assets/scss/Admin/CustomStatistics.scss';

const CustomStatistics = ({ stats, filters, onBack }) => {
    const reportRef = useRef(null);

    const handleDownloadPDF = async () => {
        const element = reportRef.current;
        const canvas = await html2canvas(element, {
            scale: 2,
            logging: false,
            useCORS: true
        });
        const imgData = canvas.toDataURL('image/png');

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;
        }

        pdf.save('statistikk-rapport.pdf');
    };

    // Prepare data for stacked bar chart
    const months = Object.keys(stats.monthlyCounts).sort();
    const packages = ['SoMePro', 'ReachPro', 'ReachElite', 'ContentPremium'];
    const packageColors = {
        'SoMePro': 'rgba(255, 99, 132, 0.7)',
        'ReachPro': 'rgba(54, 162, 235, 0.7)',
        'ReachElite': 'rgba(255, 206, 86, 0.7)',
        'ContentPremium': 'rgba(75, 192, 192, 0.7)',
    };

    const monthlyData = {
        labels: months,
        datasets: packages.map(pkg => ({
            label: pkg,
            data: months.map(month => {
                if (stats.monthlyPackageCounts && stats.monthlyPackageCounts[month]) {
                    return stats.monthlyPackageCounts[month][pkg] || 0;
                }
                return 0;
            }),
            backgroundColor: packageColors[pkg],
            stack: 'Stack 0',
        }))
    };

    const stackedOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
        },
        scales: {
            x: { stacked: true },
            y: { stacked: true },
        },
    };

    const yearlyData = {
        labels: Object.keys(stats.yearlyCounts).sort(),
        datasets: [
            {
                label: 'Årlig oversikt',
                data: Object.keys(stats.yearlyCounts).sort().map(key => stats.yearlyCounts[key]),
                backgroundColor: 'rgba(255, 159, 64, 0.6)',
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
            },
        ],
    };

    // Sort and limit office data
    const sortedOffices = Object.entries(stats.officeCounts || {})
        .sort(([, a], [, b]) => b - a);

    const officeData = {
        labels: sortedOffices.map(([key]) => key),
        datasets: [
            {
                label: 'Bestillinger per kontor',
                data: sortedOffices.map(([, value]) => value),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
        ],
    };

    // Sort and limit user data (Top 10)
    const sortedUsers = Object.entries(stats.userCounts || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);

    const userData = {
        labels: sortedUsers.map(([key]) => key),
        datasets: [
            {
                label: 'Topp 10 Bestillere',
                data: sortedUsers.map(([, value]) => value),
                backgroundColor: 'rgba(153, 102, 255, 0.6)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1,
            },
        ],
    };

    return (
        <div className="custom-statistics">
            <div className="header-actions">
                <button className="back-btn" onClick={onBack}>
                    <FaArrowLeft /> Tilbake
                </button>
                <button className="download-btn" onClick={handleDownloadPDF}>
                    <FaDownload /> Lag PDF
                </button>
            </div>

            <div className="report-container" ref={reportRef}>
                <div className="report-header">
                    <h1>Statistikkrapport</h1>
                    <div className="filter-summary">
                        <h3>Valgte filtere:</h3>
                        <ul>
                            {filters.packageChoice && filters.packageChoice.length > 0 && (
                                <li>Pakke: {filters.packageChoice.join(', ')}</li>
                            )}
                            {filters.location && filters.location.length > 0 && (
                                <li>Kontor: {filters.location.join(', ')}</li>
                            )}
                            {filters.stillbildevideo && <li>Inkl. Stillbildevideo</li>}
                            {filters.animatedAd && <li>Inkl. Animert annonse</li>}
                            {filters.hybridHome && <li>Inkl. Hybrid/Hjemmekontor</li>}
                            {filters.startDate && <li>Fra: {filters.startDate}</li>}
                            {filters.endDate && <li>Til: {filters.endDate}</li>}
                            {(!filters.packageChoice || filters.packageChoice.length === 0) &&
                                (!filters.location || filters.location.length === 0) &&
                                !filters.stillbildevideo &&
                                !filters.animatedAd &&
                                !filters.hybridHome &&
                                !filters.startDate &&
                                !filters.endDate &&
                                <li>Ingen filtere valgt (Viser alt)</li>}
                        </ul>
                    </div>
                </div>

                <div className="stats-summary-cards">
                    <div className="card">
                        <h3>Totalt antall</h3>
                        <p className="big-number">{stats.totalCount}</p>
                    </div>
                    <div className="card">
                        <h3>Snitt per bruker (Totalt)</h3>
                        <p className="big-number">{stats.averages?.formsPerUser || 0}</p>
                    </div>
                </div>



                <div className="charts-section">
                    <div className="chart-row">
                        <div className="chart-container half">
                            <h3>Månedlig utvikling</h3>
                            <Bar data={monthlyData} options={stackedOptions} />
                        </div>
                        <div className="chart-container half">
                            <h3>Årlig oversikt</h3>
                            <Bar data={yearlyData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
                        </div>
                    </div>

                    <div className="chart-row">
                        <div className="chart-container half">
                            <h3>Bestillinger per kontor</h3>
                            <Bar data={officeData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
                        </div>
                        <div className="chart-container half">
                            <h3>Topp 10 Bestillere</h3>
                            <Bar data={userData} options={{ indexAxis: 'y', responsive: true, plugins: { legend: { position: 'top' } } }} />
                        </div>
                    </div>
                </div>

                <div className="report-footer">
                    <p>Generert av ADA Admin System</p>
                    <p>Dato: {new Date().toLocaleDateString()}</p>
                </div>
            </div>
        </div>
    );
};

export default CustomStatistics;
